/* =====================================================================
   The Organic Butchery · Mega menu (Oct 2026)
   - Desktop: hover (with intent delay), keyboard and touch support for
     the Shop All, Offers and Our Story dropdowns; hovering a department
     swaps the Shop All panel.
   - Mobile: drill-down drawer. Open it from any element with the
     attribute  data-tob-mm-open-drawer  (e.g. the theme's hamburger),
     or call  window.TOBMegaMenu.openDrawer().
   No dependencies. Re-initialises in the theme editor.
   ===================================================================== */
(function () {
  'use strict';

  var OPEN_DELAY = 120;   // ms before a hovered item opens
  var CLOSE_DELAY = 220;  // ms before the menu closes after the pointer leaves
  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

  function init(root) {
    if (!root || root.__tobMm) return;
    root.__tobMm = true;

    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-mm-open]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-mm-panel]'));
    var openTimer = null;
    var closeTimer = null;
    var current = null; // { kind, trigger }

    function panelFor(kind) {
      return root.querySelector('[data-mm-panel="' + kind + '"]');
    }

    function showDept(key) {
      var shop = panelFor('shop');
      if (!shop || !key) return;
      var target = shop.querySelector('[data-mm-dept-panel="' + key + '"]');
      if (!target) return;
      shop.querySelectorAll('[data-mm-dept-panel]').forEach(function (p) { p.hidden = p !== target; });
      shop.querySelectorAll('[data-mm-dept-link]').forEach(function (a) {
        if (a.getAttribute('data-mm-dept-link') === key) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }

    function positionDrop(panel, trigger) {
      // Right-align the compact dropdown with its nav item, kept inside the viewport.
      if (!panel.classList.contains('tob-mm__drop')) return;
      var rootBox = root.getBoundingClientRect();
      var t = trigger.getBoundingClientRect();
      panel.style.right = 'auto';
      panel.style.left = '0px';
      var w = panel.offsetWidth;
      var left = t.right - rootBox.left + 24 - w;
      var max = rootBox.width - w - 16;
      panel.style.left = Math.max(16, Math.min(left, max)) + 'px';
    }

    function open(kind, trigger, dept) {
      clearTimeout(closeTimer);
      var panel = panelFor(kind);
      if (!panel) return;
      panels.forEach(function (p) { p.hidden = p !== panel; });
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', t === trigger ? 'true' : 'false'); });
      if (kind === 'shop') showDept(dept || 'd1');
      if (trigger) positionDrop(panel, trigger);
      current = { kind: kind, trigger: trigger };
    }

    function close(returnFocus) {
      clearTimeout(openTimer);
      panels.forEach(function (p) { p.hidden = true; });
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (returnFocus && current && current.trigger) current.trigger.focus();
      current = null;
    }

    function isOpen(trigger) {
      return trigger.getAttribute('aria-expanded') === 'true';
    }

    triggers.forEach(function (trigger) {
      var kind = trigger.getAttribute('data-mm-open');
      var dept = trigger.getAttribute('data-mm-dept');

      trigger.addEventListener('mouseenter', function () {
        if (!hoverCapable.matches) return;
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
        openTimer = setTimeout(function () { open(kind, trigger, dept); }, current ? 0 : OPEN_DELAY);
      });
      trigger.addEventListener('mouseleave', function () { clearTimeout(openTimer); });

      // Touch / no-hover: the first tap opens the panel, a second tap follows the link.
      trigger.addEventListener('click', function (e) {
        if (hoverCapable.matches) return;
        if (!isOpen(trigger)) { e.preventDefault(); open(kind, trigger, dept); }
      });

      // Keyboard: Arrow Down opens and moves into the panel; Space toggles.
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault();
          if (e.key === ' ' && isOpen(trigger)) { close(false); return; }
          open(kind, trigger, dept);
          var first = panelFor(kind).querySelector('a, button');
          if (e.key === 'ArrowDown' && first) first.focus();
        }
      });
    });

    // Hovering a plain nav link (e.g. Recipes) closes whatever panel is open.
    root.querySelectorAll('.tob-mm__nav-link:not([data-mm-open])').forEach(function (a) {
      a.addEventListener('mouseenter', function () {
        if (!hoverCapable.matches || !current) return;
        clearTimeout(openTimer);
        close(false);
      });
    });

    // Hovering or focusing a department swaps the panel.
    root.querySelectorAll('[data-mm-dept-link]').forEach(function (a) {
      var key = a.getAttribute('data-mm-dept-link');
      a.addEventListener('mouseenter', function () { if (hoverCapable.matches) showDept(key); });
      a.addEventListener('focus', function () { showDept(key); });
    });

    // Keep open while the pointer is anywhere over the nav row or a panel.
    root.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    root.addEventListener('mouseleave', function () {
      if (!hoverCapable.matches) return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () { close(false); }, CLOSE_DELAY);
    });

    // Esc closes an open panel wherever focus is: a panel opened by hovering
    // leaves focus outside the menu, so listening on the menu alone missed it.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !current || !document.body.contains(root)) return;
      var inside = root.contains(document.activeElement);
      close(inside);
    });

    // Close when focus or a tap leaves the menu.
    root.addEventListener('focusout', function (e) {
      if (current && e.relatedTarget && !root.contains(e.relatedTarget)) close(false);
    });
    document.addEventListener('click', function (e) {
      if (current && !root.contains(e.target)) close(false);
    });

    initDrawer(root);
    initSticky(root);
  }

  /* ------------------ Sticky under the theme header ------------------
     The theme makes its header section sticky (top: 0). When it is, this
     section sticks directly underneath, at the header's height. In the
     header's "sticky on scroll up" mode the header slides away; the menu
     then moves up to the top. */
  function initSticky(root) {
    var section = root.closest('.shopify-section');
    var header = document.querySelector('.site-header');
    var headerSection = header && header.closest('.shopify-section');
    if (!section || !headerSection) return;
    if (getComputedStyle(headerSection).position !== 'sticky') return;
    section.classList.add('is-sticky');
    var sync = function () {
      var hidden = header.classList.contains('is-hidden');
      /* exact height, floored and tucked 1px under the header (which sits above, z-index 50):
         offsetHeight rounds, so a 104.6px header left a hairline gap content showed through */
      var h = headerSection.getBoundingClientRect().height;
      section.style.setProperty('--tob-mm-top', (hidden ? 0 : Math.max(0, Math.floor(h) - 1)) + 'px');
    };
    sync();
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(headerSection);
    else window.addEventListener('resize', sync);
    if ('MutationObserver' in window) new MutationObserver(sync).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  /* ------------------------- Mobile drawer ------------------------- */
  function initDrawer(root) {
    var drawer = root.querySelector('[data-mm-drawer]');
    var scrim = root.querySelector('.tob-mm-drawer__scrim');
    if (!drawer) return;

    // Move to <body> so the theme header's stacking context can't sit on top of it.
    document.body.appendChild(drawer);
    if (scrim) document.body.appendChild(scrim);
    // Carry the design tokens across (they are defined on .tob-mm).
    drawer.classList.add('tob-mm');
    if (scrim) scrim.classList.add('tob-mm');

    var lastFocus = null;
    var history = [];

    // history holds one entry per screen: { level, scroll, from }. Going back
    // restores that screen's scroll position and refocuses the row that was tapped.
    function show(level, back) {
      drawer.querySelectorAll('[data-mm-level]').forEach(function (l) {
        l.hidden = l.getAttribute('data-mm-level') !== level;
      });
      drawer.scrollTop = back ? (back.scroll || 0) : 0;
      // Focus the back button on sub-levels, the close button on the root
      // (never the search field, which would pop the phone keyboard open).
      var first = (back && back.from) || drawer.querySelector('[data-mm-level="' + level + '"] [data-mm-back]') || drawer.querySelector('[data-mm-drawer-close]');
      if (first) first.focus({ preventScroll: true });
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      history = [{ level: 'root' }];
      drawer.hidden = false;
      if (scrim) scrim.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      show('root');
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      var done = function () { drawer.hidden = true; if (scrim) scrim.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 260);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    drawer.addEventListener('click', function (e) {
      var go = e.target.closest('[data-mm-go]');
      if (go) {
        var here = history[history.length - 1];
        if (here) { here.scroll = drawer.scrollTop; here.from = go; }
        history.push({ level: go.getAttribute('data-mm-go') });
        show(go.getAttribute('data-mm-go'));
        return;
      }
      if (e.target.closest('[data-mm-back]')) {
        history.pop();
        var prev = history[history.length - 1] || { level: 'root' };
        show(prev.level, prev);
        return;
      }
      if (e.target.closest('[data-mm-drawer-close]')) closeDrawer();
    });
    if (scrim) scrim.addEventListener('click', closeDrawer);

    drawer.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      // Keep focus inside the drawer while it is open.
      var f = Array.prototype.filter.call(
        drawer.querySelectorAll('a[href], button, input'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });

    window.TOBMegaMenu = { openDrawer: openDrawer, closeDrawer: closeDrawer, drawer: drawer };

    // The theme's burger points at this drawer while the mega menu is on.
    var burger = document.querySelector('.site-header [data-menu-open]');
    if (burger) burger.setAttribute('aria-controls', drawer.id);
  }

  /* One listener for the whole page, added once (re-initialising in the
     theme editor must not stack more). Capture phase, so it runs before the
     theme's own handler: while the mega menu is present, the theme's burger
     opens this drawer instead of the theme's menu drawer. Without the
     section, nothing is intercepted and the theme's drawer works as before. */
  if (!window.__tobMmOpener) {
    window.__tobMmOpener = true;
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-tob-mm-open-drawer], .site-header [data-menu-open]');
      var mm = window.TOBMegaMenu;
      if (!opener || !mm || !mm.drawer || !document.body.contains(mm.drawer)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      mm.openDrawer();
    }, true);
  }

  function boot() {
    document.querySelectorAll('[data-tob-mm]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: removing the section takes its drawer (which lives on
  // <body>) with it, so the theme's own burger and drawer take over again.
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target.querySelector || !e.target.querySelector('[data-tob-mm]')) return;
    document.querySelectorAll('body > [data-mm-drawer], body > .tob-mm-drawer__scrim').forEach(function (el) { el.remove(); });
    document.documentElement.style.overflow = '';
    window.TOBMegaMenu = null;
  });

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    document.querySelectorAll('body > [data-mm-drawer], body > .tob-mm-drawer__scrim').forEach(function (el) { el.remove(); });
    var root = e.target.querySelector('[data-tob-mm]');
    if (root) init(root);
  });
})();
